import React from "react";
import styles from "./StayInspired.module.scss";
import cx from "classnames";
import Instagram from "@components/Icons/Instagram";
import Facebook from "@components/Icons/Facebook";
import InstagramDark from "@components/Icons/InstagramDark";
import FacebookDark from "@components/Icons/FacebookDark";
import map from "lodash/map";
import slice from "lodash/slice";
import get from "lodash/get";
import castArray from "lodash/castArray";
import Link from "@components/Link";
import { usePageContext } from "@providers/PageProvider";
import helper from "@utils/helper";
import renderPropsComposer from "@utils/renderPropsComposer";
import usePromise from "@hooks/usePromise";
import StrapiService from "@services/StrapiService";
import { useTranslationContext } from "@providers/TranslationProvider";

const STAY_CONNECTED_IDS: Record<string, string> = {
  "amanaki-thao-dien": "hotel10-stay-connected",
  "amanaki-boutique-saigon": "hotel65-stay-connected",
};

const GROUP_HOME_SECTION_TITLES: Record<string, string> = {
  "amanaki-thao-dien": "Amanaki Thao Dien",
  "amanaki-boutique-saigon": "Amanaki Saigon Boutique",
};

const CardFlipWrapper = ({
  children,
  delay,
  className,
}: {
  children: React.ReactNode;
  delay: number;
  className: string;
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [inView, setInView] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{ transitionDelay: `${delay}ms` } as React.CSSProperties}
      data-in-view={inView}
    >
      {children}
    </div>
  );
};

const renderIcons = (data: any, type?: string) => {
  return map(castArray(data), (icon, index) => {
    return renderPropsComposer(
      {
        matcher: ({ type }: any) => type === "instagram",
        render: ({ href }: any) => (
          <Link
            key={`icon_${index}`}
            href={href}
            target="_blank"
            rel="noopener"
            ariaLabel={"amanaki instagram"}
          >
            {type === "dark" && <InstagramDark />}
            {type !== "dark" && <Instagram />}
          </Link>
        ),
      },
      {
        matcher: ({ type }: any) => type === "facebook",
        render: ({ href }: any) => (
          <Link
            href={href}
            target="_blank"
            rel="noopener"
            key={`icon_${index}`}
            ariaLabel={"amanaki facebook"}
          >
            {type === "dark" && <FacebookDark />}
            {type !== "dark" && <Facebook />}
          </Link>
        ),
      }
    )(icon);
  });
};
const renderImages = (data: any) => {
  return map(data, (item, index) => {
    const src = get(item, "image.src", "");
    if (!src) return null;
    return (
      <Link
        href={get(item, "icon.href")}
        key={`image_${index}`}
        target="_blank"
        rel="noopener"
        ariaLabel={get(item, "image.alt") || "amanaki image"}
      >
        <CardFlipWrapper
          delay={Number(index) * 80}
          className={cx(styles.wrapperImage, styles.cardFlip)}
        >
          <div className={cx(styles.wrapper)} style={{ position: "relative" }}>
            <img
              src={src}
              alt={get(item, "image.alt", "")}
              className={cx(styles.image)}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </div>
          <div className={cx(styles.icon)}>
            {!!(get(item, "icon.type") === "instagram") && <Instagram />}
            {!!(get(item, "icon.type") === "facebook") && <Facebook />}
          </div>
        </CardFlipWrapper>
      </Link>
    );
  });
};

const InstagramSection = ({
  stayConnected,
  title,
  mode,
  border,
  hideDivider,
  noScrollSection,
}: {
  stayConnected: any;
  title: string;
  mode: string;
  border: string;
  hideDivider?: boolean;
  noScrollSection?: boolean;
}) => (
  <section
    {...(noScrollSection ? {} : { "data-scroll-section": true })}
    className={cx("", styles.section, styles[mode])}
  >
    <div
      className={cx("divider", {
        hidden: border === "none" || hideDivider,
      })}
    ></div>
    <div className={cx("inner-large", styles.boxTitle)}>
      <h2 className="ama-section-title">{title}</h2>
      <div className={cx(styles.groupSocial, styles.mobile)}>
        {renderIcons(get(stayConnected, "icons"))}
      </div>
      <div className={cx(styles.groupSocialDark, styles.mobile)}>
        {renderIcons(get(stayConnected, "icons"), "dark")}
      </div>
      <div className={cx("ama-sub-title", styles.description)}>
        {get(stayConnected, "description", "")}
      </div>
      <div className={cx(styles.groupSocial, styles.desktop)}>
        {renderIcons(get(stayConnected, "icons"))}
      </div>
      <div className={cx(styles.groupSocialDark, styles.desktop)}>
        {renderIcons(get(stayConnected, "icons"), "dark")}
      </div>
    </div>
    <div className={cx("inner-large", styles.box)}>
      {renderImages(slice(get(stayConnected, "images"), 0, 5))}
    </div>
    <div className={cx("inner-large", styles.box, styles.mobile)}>
      {renderImages(get(stayConnected, "images"))}
    </div>
  </section>
);

const View = ({ mode = "", border = "" }: any) => {
  const { i18n } = useTranslationContext();
  const { hotel, stayConnected: stayConnectedFromContext } = usePageContext();
  const isGroupHome = !hotel;

  const [clientData] = usePromise(
    async () => {
      if (isGroupHome) {
        return null;
      }
      const hotelId = hotel ?? "amanaki-thao-dien";
      StrapiService.set("id", STAY_CONNECTED_IDS[hotelId]);
      const resInspired = await StrapiService.find({
        populate: "deep",
        locale: i18n.language,
      });
      return get(resInspired, "data", {});
    },
    undefined,
    [i18n.language, hotel]
  );

  const data = isGroupHome
    ? stayConnectedFromContext
    : clientData;

  const isGroupHomeData =
    data &&
    typeof data === "object" &&
    "amanaki-thao-dien" in data &&
    "amanaki-boutique-saigon" in data;

  if (isGroupHomeData) {
    const thaoDien = helper.formatStayConnected({
      stayConnected: data["amanaki-thao-dien"],
    });
    const boutique = helper.formatStayConnected({
      stayConnected: data["amanaki-boutique-saigon"],
    });
    return (
      <section
        data-scroll-section
        className={cx("", styles.section, styles[mode])}
      >
        <div className={cx("divider", { hidden: border === "none" })} />
        <h2 className={cx("ama-section-title", styles.stayConnectedTitle)}>
          Stay Connected
        </h2>
        <InstagramSection
          stayConnected={thaoDien}
          title={GROUP_HOME_SECTION_TITLES["amanaki-thao-dien"]}
          mode={mode}
          border={border}
          hideDivider
          noScrollSection
        />
        <InstagramSection
          stayConnected={boutique}
          title={GROUP_HOME_SECTION_TITLES["amanaki-boutique-saigon"]}
          mode={mode}
          border={border}
          hideDivider
          noScrollSection
        />
      </section>
    );
  }

  const stayConnected = helper.formatStayConnected({ stayConnected: data });
  return (
    <InstagramSection
      stayConnected={stayConnected}
      title={get(stayConnected, "title", "")}
      mode={mode}
      border={border}
    />
  );
};
export default View;
